/** TopBar: the breadcrumb on the left (with the button that brings a hidden sidebar back) and icon buttons on the right. */
import { Fragment, type ReactNode } from "react";
import { useShell } from "./AppShell";
import { Button } from "./Button";
import { cx, focusRing } from "./cx";
import { Graphic, Icon, type IconName } from "./icons";
import { Tooltip } from "./Tooltip";

export function TopBar({ breadcrumb, children }: { breadcrumb: ReactNode; children?: ReactNode }) {
  const { sidebarHidden, showSidebar } = useShell();
  return (
    <header className="flex h-topbar w-full items-center justify-between px-3 py-3.5">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={showSidebar}
          aria-label="Open sidebar"
          className={cx("-my-2.5 -ml-2.5 flex size-9 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-black/[0.05] lg:-my-2 lg:-ml-2 lg:size-8", focusRing, !sidebarHidden && "lg:hidden")}
        >
          <Icon name="sidebar" className="rotate-180" />
        </button>
        {breadcrumb}
      </div>
      <div className="flex items-center gap-3">{children}</div>
    </header>
  );
}

interface Crumb {
  label: string;
  icon?: IconName;
  href?: string;
}

/** The last crumb is the current page; the ones before it are links. */
export function Breadcrumb({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-4">
      {items.map((item, index) =>
        index === items.length - 1 ? (
          <span key={item.label} aria-current="page" className="whitespace-nowrap text-body font-medium leading-none text-foreground">
            {item.label}
          </span>
        ) : (
          <Fragment key={item.label}>
            <a href={item.href ?? "#"} className={cx("group/bc flex items-center gap-2.5 rounded-md", focusRing)}>
              {item.icon && <Icon name={item.icon} />}
              <span className="whitespace-nowrap text-body leading-none text-muted-foreground transition-colors group-hover/bc:text-brand">{item.label}</span>
            </a>
            <span className="relative h-2.5 w-1.5 shrink-0" aria-hidden>
              <span className="pointer-events-none absolute block" style={{ inset: "-2.57% -7.15%" }}>
                <Graphic name="slash" className="block size-full max-w-none" />
              </span>
            </span>
          </Fragment>
        ),
      )}
    </nav>
  );
}

const MOTION = {
  /** Swings from its top like a bell. */
  ring: "origin-top transition-transform group-hover/tb:animate-ring",
  /** A quarter turn, like a cog. */
  turn: "transition-transform duration-500 ease-out-expo group-hover/tb:rotate-90",
};

interface TopBarButtonProps {
  label: string;
  icon: IconName;
  motion?: keyof typeof MOTION;
  onClick?: () => void;
}

/** An icon button with its label as a tooltip. */
export function TopBarButton({ label, icon, motion, onClick }: TopBarButtonProps) {
  return (
    <Tooltip content={label}>
      <Button variant="ghost" size="icon-sm" aria-label={label} onClick={onClick} className="group/tb -m-1">
        <Icon name={icon} className={motion && MOTION[motion]} />
      </Button>
    </Tooltip>
  );
}
